"use client";

import Link from "next/link";
import { useState, useRef, useEffect } from "react";
import ThemeToggle from "./ThemeToggle";
import NavDropdown, { FlatItem, GroupedItem } from "./NavDropdown";

const siteName = process.env.NEXT_PUBLIC_SITE_NAME ?? "Meazora";

const diyItems: FlatItem[] = [
  { label: "Concrete calculator", href: "/calculators/concrete/", icon: "concrete" },
  { label: "Paint calculator", href: "/calculators/paint/", icon: "paint" },
  { label: "Flooring calculator", href: "/calculators/flooring/", icon: "flooring" },
  { label: "Square footage calculator", href: "/calculators/area/", icon: "area" },
  { label: "Fence calculator", href: "/calculators/fence/", icon: "fence" },
  { label: "Mulch and gravel calculator", href: "/calculators/mulch/", icon: "mulch" },
  { label: "Drywall calculator", href: "/calculators/drywall/", icon: "drywall" },
  { label: "Tile calculator", href: "/calculators/tile/", icon: "tile" },
  { label: "Roof shingle calculator", href: "/calculators/roof-shingles/", icon: "roof-shingles" },
  { label: "Insulation calculator", href: "/calculators/insulation/", icon: "insulation" },
  { label: "Siding material calculator", href: "/calculators/siding-material/", icon: "siding" },
];

const estimatorGroups: GroupedItem[] = [
  {
    groupLabel: "Interior cost",
    groupColor: "var(--card-accent-interior)",
    items: [
      { label: "Kitchen remodel cost", href: "/calculators/kitchen/" },
      { label: "Bathroom remodel cost", href: "/calculators/bathroom/" },
      { label: "Remodeling ROI", href: "/calculators/roi/" },
      { label: "Home addition cost", href: "/calculators/home-addition/" },
      { label: "Basement finishing cost", href: "/calculators/basement-finishing/" },
      { label: "Countertop cost calculator", href: "/calculators/countertop/" },
      { label: "Cabinet refacing cost", href: "/calculators/cabinet-refacing/" },
    ],
  },
  {
    groupLabel: "Exterior cost",
    groupColor: "var(--card-accent-exterior)",
    items: [
      { label: "Roof replacement cost", href: "/calculators/roofing/" },
      { label: "Window replacement cost", href: "/calculators/windows/" },
      { label: "Deck cost calculator", href: "/calculators/deck/" },
      { label: "Siding cost calculator", href: "/calculators/siding/" },
      { label: "Driveway paving cost", href: "/calculators/driveway/" },
      { label: "Swimming pool cost", href: "/calculators/pool/" },
      { label: "Gutter installation cost", href: "/calculators/gutters/" },
      { label: "Retaining wall cost", href: "/calculators/retaining-wall/" },
    ],
  },
  {
    groupLabel: "Systems and energy",
    groupColor: "var(--card-accent-systems)",
    items: [
      { label: "Solar savings calculator", href: "/calculators/solar/" },
      { label: "HVAC replacement cost", href: "/calculators/hvac/" },
      { label: "Water heater replacement", href: "/calculators/water-heater/" },
      { label: "Backup generator sizing", href: "/calculators/backup-generator/" },
      { label: "EV charger installation", href: "/calculators/ev-charger/" },
    ],
  },
];

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileDiyOpen, setMobileDiyOpen] = useState(false);
  const [mobileEstimatorsOpen, setMobileEstimatorsOpen] = useState(false);

  const [activeDropdown, setActiveDropdown] = useState<"diy" | "estimators" | null>(null);

  const openTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const diyButtonRef = useRef<HTMLButtonElement>(null);
  const estimatorsButtonRef = useRef<HTMLButtonElement>(null);

  const handleMouseEnter = (dropdown: "diy" | "estimators") => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }

    if (activeDropdown && activeDropdown !== dropdown) {
      if (openTimeoutRef.current) {
        clearTimeout(openTimeoutRef.current);
        openTimeoutRef.current = null;
      }
      setActiveDropdown(dropdown);
    } else if (activeDropdown === null) {
      if (openTimeoutRef.current) clearTimeout(openTimeoutRef.current);
      openTimeoutRef.current = setTimeout(() => {
        setActiveDropdown(dropdown);
      }, 180);
    }
  };

  const handleMouseLeave = () => {
    if (openTimeoutRef.current) {
      clearTimeout(openTimeoutRef.current);
      openTimeoutRef.current = null;
    }
    if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    closeTimeoutRef.current = setTimeout(() => {
      setActiveDropdown(null);
    }, 180);
  };

  // Close dropdown on click outside or escape key press
  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      const header = document.querySelector("header");
      if (header && !header.contains(e.target as Node)) {
        setActiveDropdown(null);
      }
    };

    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setActiveDropdown(null);
      }
    };

    document.addEventListener("click", handleOutsideClick);
    document.addEventListener("keydown", handleEscapeKey);

    return () => {
      document.removeEventListener("click", handleOutsideClick);
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, []);

  return (
    <header className="sticky top-0 z-50 w-full border-b-2 border-[var(--foreground)] bg-[var(--surface)] transition-colors h-[62px] flex items-stretch">
      <div className="w-full mx-auto px-4 md:px-12 flex items-stretch justify-between relative">
        {/* Logo */}
        <Link href="/" className="flex items-center gap-3 group transition-opacity hover:opacity-90 py-2">
          {/* Logo: house outline + ruler ticks (House Blueprint V1) */}
          <svg
            viewBox="0 0 32 32"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="w-[22px] h-[22px] shrink-0 text-[var(--accent)]"
            aria-hidden="true"
          >
            {/* Roof */}
            <polyline
              points="2,17 16,4 30,17"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            {/* Walls */}
            <rect
              x="7" y="17" width="18" height="11"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinejoin="round"
            />
            {/* Ruler ticks */}
            <line x1="10" y1="25" x2="10" y2="27.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
            <line x1="13" y1="26.2" x2="13" y2="27.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
            <line x1="16" y1="25" x2="16" y2="27.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
            <line x1="19" y1="26.2" x2="19" y2="27.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
            <line x1="22" y1="25" x2="22" y2="27.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
          </svg>
          <span className="font-heading-condensed font-extrabold uppercase tracking-wide text-[var(--ink)] text-[21px] leading-none">
            {siteName}
          </span>
          <span className="font-mono font-normal text-[10px] tracking-wider text-[var(--ink-soft)] border-l border-[var(--line-strong)] pl-[11px] ml-[1px] hidden sm:block leading-[1.3] text-left uppercase">
            CONSTRUCTION &<br />REMODELING CALCULATORS
          </span>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-stretch mr-[-48px]">
          <nav className="flex items-stretch" aria-label="Main navigation">
            {/* DIY Dropdown */}
            <div
              className="relative flex items-stretch border-l border-[var(--line)]"
              onMouseEnter={() => handleMouseEnter("diy")}
              onMouseLeave={handleMouseLeave}
            >
              <button
                ref={diyButtonRef}
                aria-expanded={activeDropdown === "diy"}
                aria-haspopup="true"
                className="px-[22px] text-xs font-mono font-bold uppercase tracking-wider text-[var(--ink-soft)] hover:text-[var(--accent)] hover:bg-[var(--accent-tint)] flex items-center gap-1.5 transition-colors h-full cursor-pointer border-none bg-transparent"
                onClick={() => {
                  setActiveDropdown(activeDropdown === "diy" ? null : "diy");
                }}
                onKeyDown={(e) => {
                  if (e.key === " " || e.key === "Enter") {
                    e.preventDefault();
                    setActiveDropdown(activeDropdown === "diy" ? null : "diy");
                  } else if (e.key === "Escape") {
                    e.preventDefault();
                    setActiveDropdown(null);
                  }
                }}
              >
                <span>DIY Calculators</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="11"
                  height="11"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className={`transition-transform duration-200 ${
                    activeDropdown === "diy" ? "rotate-180" : ""
                  }`}
                >
                  <path d="m6 9 6 6 6-6" />
                </svg>
              </button>
              <NavDropdown
                items={diyItems}
                isOpen={activeDropdown === "diy"}
                onMouseEnter={() => handleMouseEnter("diy")}
                onMouseLeave={handleMouseLeave}
                onItemClick={() => setActiveDropdown(null)}
                onEscape={() => {
                  setActiveDropdown(null);
                  diyButtonRef.current?.focus();
                }}
              />
            </div>

            {/* Cost Estimators Dropdown */}
            <div
              className="relative flex items-stretch border-l border-[var(--line)]"
              onMouseEnter={() => handleMouseEnter("estimators")}
              onMouseLeave={handleMouseLeave}
            >
              <button
                ref={estimatorsButtonRef}
                aria-expanded={activeDropdown === "estimators"}
                aria-haspopup="true"
                className="px-[22px] text-xs font-mono font-bold uppercase tracking-wider text-[var(--ink-soft)] hover:text-[var(--accent)] hover:bg-[var(--accent-tint)] flex items-center gap-1.5 transition-colors h-full cursor-pointer border-none bg-transparent"
                onClick={() => {
                  setActiveDropdown(activeDropdown === "estimators" ? null : "estimators");
                }}
                onKeyDown={(e) => {
                  if (e.key === " " || e.key === "Enter") {
                    e.preventDefault();
                    setActiveDropdown(activeDropdown === "estimators" ? null : "estimators");
                  } else if (e.key === "Escape") {
                    e.preventDefault();
                    setActiveDropdown(null);
                  }
                }}
              >
                <span>Cost Estimators</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="11"
                  height="11"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className={`transition-transform duration-200 ${
                    activeDropdown === "estimators" ? "rotate-180" : ""
                  }`}
                >
                  <path d="m6 9 6 6 6-6" />
                </svg>
              </button>
              <NavDropdown
                groups={estimatorGroups}
                isOpen={activeDropdown === "estimators"}
                align="right"
                onMouseEnter={() => handleMouseEnter("estimators")}
                onMouseLeave={handleMouseLeave}
                onItemClick={() => setActiveDropdown(null)}
                onEscape={() => {
                  setActiveDropdown(null);
                  estimatorsButtonRef.current?.focus();
                }}
              />
            </div>

            {/* Calculators Hub Link */}
            <Link
              href="/calculators/"
              prefetch={false}
              className="px-[22px] text-xs font-mono font-bold uppercase tracking-wider text-[var(--ink-soft)] hover:text-[var(--accent)] hover:bg-[var(--accent-tint)] border-l border-[var(--line)] flex items-center transition-colors h-full"
            >
              All Calculators
            </Link>

            {/* Guides Link */}
            <Link
              href="/articles/"
              prefetch={false}
              className="px-[22px] text-xs font-mono font-bold uppercase tracking-wider text-[var(--ink-soft)] hover:text-[var(--accent)] hover:bg-[var(--accent-tint)] border-l border-[var(--line)] flex items-center transition-colors h-full"
            >
              Guides
            </Link>

            <div className="border-l border-r border-[var(--line)] flex items-center px-6 h-full">
              <ThemeToggle />
            </div>
          </nav>
        </div>

        {/* Mobile menu and toggle */}
        <div className="flex items-center gap-2 md:hidden">
          <ThemeToggle />
          <button
            id="mobile-menu-button"
            className="p-2 rounded-lg text-[var(--ink-soft)] hover:bg-[var(--surface-muted)] transition-colors cursor-pointer border-none bg-transparent"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle mobile menu"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              {mobileOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile nav drawer */}
      {mobileOpen && (
        <nav className="absolute top-[62px] left-0 w-full md:hidden border-b border-[var(--line)] bg-[var(--surface)] py-3 px-4 space-y-3 z-50 max-h-[85vh] overflow-y-auto" aria-label="Mobile navigation">
          {/* DIY Calculators Accordion */}
          <div className="border-b border-[var(--line)] pb-2.5">
            <button
              onClick={() => setMobileDiyOpen(!mobileDiyOpen)}
              aria-expanded={mobileDiyOpen}
              className="w-full flex items-center justify-between py-1 text-xs font-mono font-bold uppercase tracking-wider text-[var(--ink-soft)] hover:text-[var(--accent)] cursor-pointer border-none bg-transparent"
            >
              <span>DIY Calculators</span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                className={`transition-transform duration-200 ${mobileDiyOpen ? "rotate-180" : ""}`}
              >
                <path d="m6 9 6 6 6-6" />
              </svg>
            </button>
            {mobileDiyOpen && (
              <div className="mt-2 pl-3 flex flex-col gap-2 border-l border-[var(--line-strong)] text-left">
                {diyItems.map((item) => (
                  <Link
                    key={item.href}
                    href={item.href}
                    onClick={() => setMobileOpen(false)}
                    className="py-1 text-[11.5px] font-sans font-semibold text-[var(--ink-soft)] hover:text-[var(--accent)]"
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Cost Estimators Accordion */}
          <div className="border-b border-[var(--line)] pb-2.5">
            <button
              onClick={() => setMobileEstimatorsOpen(!mobileEstimatorsOpen)}
              aria-expanded={mobileEstimatorsOpen}
              className="w-full flex items-center justify-between py-1 text-xs font-mono font-bold uppercase tracking-wider text-[var(--ink-soft)] hover:text-[var(--accent)] cursor-pointer border-none bg-transparent"
            >
              <span>Cost Estimators</span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                className={`transition-transform duration-200 ${mobileEstimatorsOpen ? "rotate-180" : ""}`}
              >
                <path d="m6 9 6 6 6-6" />
              </svg>
            </button>
            {mobileEstimatorsOpen && (
              <div className="mt-2 pl-3 flex flex-col gap-3 border-l border-[var(--line-strong)] text-left">
                {estimatorGroups.map((group) => (
                  <div key={group.groupLabel} className="flex flex-col gap-1.5">
                    <span
                      className="text-[9.5px] font-mono font-bold uppercase tracking-wider"
                      style={{ color: group.groupColor }}
                    >
                      {group.groupLabel}
                    </span>
                    <div className="flex flex-col gap-1.5 pl-2">
                      {group.items.map((item) => (
                        <Link
                          key={item.href}
                          href={item.href}
                          onClick={() => setMobileOpen(false)}
                          className="py-0.5 text-[11px] font-sans font-semibold text-[var(--ink-soft)] hover:text-[var(--accent)]"
                        >
                          {item.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Calculators Hub Link */}
          <div className="text-left border-b border-[var(--line)] pb-2.5">
            <Link
              href="/calculators/"
              className="block py-1 text-xs font-mono font-bold uppercase tracking-wider text-[var(--ink-soft)] hover:text-[var(--accent)]"
              onClick={() => setMobileOpen(false)}
            >
              All Calculators
            </Link>
          </div>

          {/* Guides Link */}
          <div className="text-left">
            <Link
              href="/articles/"
              className="block py-1 text-xs font-mono font-bold uppercase tracking-wider text-[var(--ink-soft)] hover:text-[var(--accent)]"
              onClick={() => setMobileOpen(false)}
            >
              Guides
            </Link>
          </div>
        </nav>
      )}
    </header>
  );
}
